import type { ApiClient } from '../client';
import type {
  BulkEmailDocumentsRequest,
  BulkGenerateAdmitCardsJobRequest,
  BulkGenerateReportCardsRequest,
  BulkJobArtifactUrlResponse,
  BulkJobStatusResponse,
  DocumentPdfExportRequest,
  EnqueueBulkJobResponse,
  ListBulkJobAcknowledgementsResponse,
  Uuid,
} from '../types';
import type { ApiResponse } from '../types';

export function bindBulkJobs(client: ApiClient) {
  return {
    async enqueueReportCardsBulkGenerate(
      body: BulkGenerateReportCardsRequest
    ): Promise<ApiResponse<EnqueueBulkJobResponse>> {
      return client.request<EnqueueBulkJobResponse>('/bulk-jobs/report-cards/generate', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async enqueueAdmitCardsBulkGenerate(
      body: BulkGenerateAdmitCardsJobRequest
    ): Promise<ApiResponse<EnqueueBulkJobResponse>> {
      return client.request<EnqueueBulkJobResponse>('/bulk-jobs/admit-cards/generate', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async enqueueDocumentsEmail(
      body: BulkEmailDocumentsRequest
    ): Promise<ApiResponse<EnqueueBulkJobResponse>> {
      return client.request<EnqueueBulkJobResponse>('/bulk-jobs/documents/email', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async enqueueDocumentPdfExport(
      body: DocumentPdfExportRequest
    ): Promise<ApiResponse<EnqueueBulkJobResponse>> {
      return client.request<EnqueueBulkJobResponse>('/bulk-jobs/documents/export-pdf', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async getBulkJob(jobId: Uuid): Promise<ApiResponse<BulkJobStatusResponse>> {
      return client.request<BulkJobStatusResponse>(`/bulk-jobs/${encodeURIComponent(jobId)}`);
    },

    async getBulkJobArtifactUrl(jobId: Uuid): Promise<ApiResponse<BulkJobArtifactUrlResponse>> {
      return client.request<BulkJobArtifactUrlResponse>(
        `/bulk-jobs/${encodeURIComponent(jobId)}/artifact-url`
      );
    },

    /** Downloads the artifact and the server deletes it afterward. */
    async downloadBulkJobArtifact(jobId: Uuid): Promise<Blob> {
      const path = `/bulk-jobs/${encodeURIComponent(jobId)}/artifact`;
      const url = `${client.baseUrl}${path}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: client.authHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Artifact download failed: ${response.status}`);
      }
      return response.blob();
    },

    async listBulkJobAcknowledgements(
      jobId: Uuid
    ): Promise<ApiResponse<ListBulkJobAcknowledgementsResponse>> {
      return client.request<ListBulkJobAcknowledgementsResponse>(
        `/bulk-jobs/${encodeURIComponent(jobId)}/acknowledgements`
      );
    },
  };
}
